import React, { useEffect, useState } from 'react';
import { followups as followupsApi } from '@/utils/api';
import { Sparkles, X, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * FollowupDrafter — AI-drafts a follow-up email for a Today item and lets the
 * operator edit + send.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   kind: 'invoice' | 'bug' | 'lead' | 'change_request'
 *   id: string  (the item id)
 *   itemLabel?: string  (optional, for modal title)
 */
export default function FollowupDrafter({ open, onClose, kind, id, itemLabel }) {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState({ subject: '', body: '', to_email: '', project_id: null, id: null });
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !kind || !id) return;
    setLoading(true); setError('');
    followupsApi.draftForItem(kind, id)
      .then(r => setDraft({
        subject: r.data.subject || '',
        body: r.data.body || '',
        to_email: r.data.to_email || '',
        project_id: r.data.project_id || null,
        id: r.data.id,
      }))
      .catch(e => setError(e.response?.data?.detail || 'Failed to draft'))
      .finally(() => setLoading(false));
  }, [open, kind, id]);

  const send = async () => {
    if (!draft.to_email) return toast.error('Recipient email required');
    if (!draft.subject.trim()) return toast.error('Subject required');
    setSending(true);
    try {
      const r = await followupsApi.send({
        draft_id: draft.id,
        to_email: draft.to_email,
        subject: draft.subject,
        body: draft.body,
        project_id: draft.project_id,
      });
      if (r.data.status === 'sent') {
        toast.success('Follow-up sent');
        onClose();
      } else {
        toast.error(r.data.error || 'Send failed');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    finally { setSending(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }} data-testid="followup-drafter">
      <div className="card-base w-full max-w-2xl p-5 animate-fade-in">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-btn flex items-center justify-center" style={{ background: '#5B2D8E20' }}>
              <Sparkles size={14} style={{ color: '#5B2D8E' }} />
            </div>
            <div>
              <h3 className="font-black text-lg" style={{ color: 'var(--ink)' }}>AI follow-up draft</h3>
              <p className="text-xs" style={{ color: 'var(--muted)' }}>
                {kind}{itemLabel ? ` · ${itemLabel}` : ''}
              </p>
            </div>
          </div>
          <button onClick={onClose} style={{ color: 'var(--muted)' }} data-testid="followup-close"><X size={16} /></button>
        </div>

        {loading ? (
          <div className="py-12 flex items-center justify-center gap-2" style={{ color: 'var(--muted)' }}>
            <Loader2 size={16} className="animate-spin" /> Drafting with Claude…
          </div>
        ) : error ? (
          <div className="card-base p-4" style={{ background: '#FCE9E5', color: '#B34A3A' }}>
            <p className="text-sm font-semibold">{error}</p>
            <p className="text-xs mt-1">Check your Claude API key in Settings → Integrations.</p>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>To</label>
              <input
                className="input-agency text-sm"
                type="email"
                value={draft.to_email}
                onChange={e => setDraft({ ...draft, to_email: e.target.value })}
                data-testid="followup-to"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Subject</label>
              <input
                className="input-agency text-sm"
                value={draft.subject}
                onChange={e => setDraft({ ...draft, subject: e.target.value })}
                data-testid="followup-subject"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Body</label>
              <textarea
                className="input-agency text-sm resize-none font-mono leading-relaxed"
                rows={10}
                value={draft.body}
                onChange={e => setDraft({ ...draft, body: e.target.value })}
                data-testid="followup-body"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <button type="button" className="flex-1 py-2 text-sm rounded-btn border font-medium" style={{ borderColor: 'var(--border)', color: 'var(--muted)' }} onClick={onClose}>Cancel</button>
              <button
                type="button"
                className="flex-1 btn-primary py-2 text-sm flex items-center justify-center gap-1.5"
                onClick={send}
                disabled={sending || !draft.subject.trim() || !draft.to_email}
                data-testid="followup-send"
              >
                <Send size={13} /> {sending ? 'Sending…' : 'Send via email'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
